import { supabase } from '../lib/supabase';
import type { PlayerDevelopmentNote } from '../types/playerCard';

/**
 * Service for managing Player Development Notes
 * Handles timestamped development notes log
 */
class PlayerDevelopmentNotesService {
  /**
   * Get all development notes for a player card
   */
  async getNotes(
    cardId: string,
    limit?: number
  ): Promise<PlayerDevelopmentNote[]> {
    let query = supabase
      .from('player_development_notes')
      .select('*')
      .eq('player_card_id', cardId)
      .order('created_at', { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[PlayerDevelopmentNotesService] Error fetching notes:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get a specific note
   */
  async getNote(noteId: string): Promise<PlayerDevelopmentNote | null> {
    const { data, error } = await supabase
      .from('player_development_notes')
      .select('*')
      .eq('id', noteId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      console.error('[PlayerDevelopmentNotesService] Error fetching note:', error);
      throw error;
    }

    return data;
  }

  /**
   * Create a new development note
   */
  async createNote(
    cardId: string,
    note: {
      note_text: string;
      category?: string;
      tags?: string[];
    },
    userId?: string
  ): Promise<PlayerDevelopmentNote> {
    // Get current user if not provided
    let currentUserId = userId;
    if (!currentUserId) {
      const { data: { user } } = await supabase.auth.getUser();
      currentUserId = user?.id;
    }

    const { data, error } = await supabase
      .from('player_development_notes')
      .insert({
        player_card_id: cardId,
        note_text: note.note_text,
        category: note.category || null,
        tags: note.tags || [],
        created_by: currentUserId || null,
      })
      .select()
      .single();

    if (error) {
      console.error('[PlayerDevelopmentNotesService] Error creating note:', error);
      throw error;
    }

    return data;
  }

  /**
   * Update a development note
   */
  async updateNote(
    noteId: string,
    updates: {
      note_text?: string;
      category?: string;
      tags?: string[];
    }
  ): Promise<PlayerDevelopmentNote> {
    const { data, error } = await supabase
      .from('player_development_notes')
      .update({
        note_text: updates.note_text,
        category: updates.category !== undefined ? updates.category : undefined,
        tags: updates.tags !== undefined ? updates.tags : undefined,
      })
      .eq('id', noteId)
      .select()
      .single();

    if (error) {
      console.error('[PlayerDevelopmentNotesService] Error updating note:', error);
      throw error;
    }

    return data;
  }

  /**
   * Delete a development note
   */
  async deleteNote(noteId: string): Promise<void> {
    const { error } = await supabase
      .from('player_development_notes')
      .delete()
      .eq('id', noteId);

    if (error) {
      console.error('[PlayerDevelopmentNotesService] Error deleting note:', error);
      throw error;
    }
  }

  /**
   * Get notes by category
   */
  async getNotesByCategory(
    cardId: string,
    category: string
  ): Promise<PlayerDevelopmentNote[]> {
    const { data, error } = await supabase
      .from('player_development_notes')
      .select('*')
      .eq('player_card_id', cardId)
      .eq('category', category)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[PlayerDevelopmentNotesService] Error fetching notes by category:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get notes by tag
   */
  async getNotesByTag(
    cardId: string,
    tag: string
  ): Promise<PlayerDevelopmentNote[]> {
    const { data, error } = await supabase
      .from('player_development_notes')
      .select('*')
      .eq('player_card_id', cardId)
      .contains('tags', [tag])
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[PlayerDevelopmentNotesService] Error fetching notes by tag:', error);
      throw error;
    }

    return data || [];
  }
}

export const playerDevelopmentNotesService = new PlayerDevelopmentNotesService();

